import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Target,
  TrendingUp,
  DollarSign,
  Users,
  Package,
  Calendar,
  X,
  ChevronRight,
  CheckCircle,
  Clock,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const goalCategories = [
  { id: 'revenue', label: 'Revenue', icon: DollarSign, color: 'bg-green-500' },
  { id: 'hiring', label: 'Hiring', icon: Users, color: 'bg-blue-500' },
  { id: 'equipment', label: 'Equipment', icon: Package, color: 'bg-purple-500' },
  { id: 'projects', label: 'Projects', icon: Target, color: 'bg-orange-500' },
  { id: 'marketing', label: 'Marketing', icon: TrendingUp, color: 'bg-pink-500' },
  { id: 'other', label: 'Other', icon: CheckCircle, color: 'bg-gray-500' }
];

const periods = ['monthly', 'quarterly', 'yearly'];

export const BusinessPlan = () => {
  const { api, isCOO } = useAuth();
  const [businessPlan, setBusinessPlan] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGoal, setShowAddGoal] = useState(false);

  const fetchData = async () => {
    if (!isCOO) return;
    try {
      const [planRes, goalsRes] = await Promise.all([
        api.get('/portal/business-plan'),
        api.get('/portal/goals')
      ]);
      setBusinessPlan(planRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Error fetching business plan:', error);
      toast.error('Failed to load business plan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isCOO]);

  const handleAddGoal = async (goalData) => {
    try {
      await api.post('/portal/goals', goalData);
      toast.success('Goal created');
      fetchData();
      setShowAddGoal(false);
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const getCategoryConfig = (categoryId) => 
    goalCategories.find(c => c.id === categoryId) || goalCategories[goalCategories.length - 1];

  // Redirect if not COO
  if (!isCOO) {
    return <Navigate to="/portal" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const projections = businessPlan?.projections || {};
  const scenarios = businessPlan?.scenarios || {};

  return (
    <div className="space-y-6" data-testid="business-plan-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Business Plan</h1>
          <p className="text-gray-500 mt-1">Strategic planning & goal tracking</p>
        </div>
        <button
          onClick={() => setShowAddGoal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          data-testid="add-goal-btn"
        >
          <Plus className="w-4 h-4" />
          Add Goal
        </button>
      </div>

      {/* Revenue Projections */}
      <div className="bg-white border border-gray-100 p-6">
        <h2 className="font-heading text-xl mb-6">Revenue Projections</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-100 p-4"
          >
            <p className="text-xs font-semibold uppercase text-green-600 mb-1">Confirmed Revenue</p>
            <p className="font-heading text-2xl text-green-700">${(projections.confirmed_revenue || 0).toLocaleString()}</p>
            <p className="text-xs text-green-600 mt-1">Active jobs</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-yellow-50 border border-yellow-100 p-4"
          >
            <p className="text-xs font-semibold uppercase text-yellow-600 mb-1">Pipeline Revenue</p>
            <p className="font-heading text-2xl text-yellow-700">${(projections.pipeline_revenue || 0).toLocaleString()}</p>
            <p className="text-xs text-yellow-600 mt-1">Pursuing & bids</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-blue-50 border border-blue-100 p-4"
          >
            <p className="text-xs font-semibold uppercase text-blue-600 mb-1">Projected Total</p>
            <p className="font-heading text-2xl text-blue-700">${(projections.total_projected || 0).toLocaleString()}</p>
            <p className="text-xs text-blue-600 mt-1">30% pipeline conversion</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-red-50 border border-red-100 p-4"
          >
            <p className="text-xs font-semibold uppercase text-red-600 mb-1">Monthly Expenses</p>
            <p className="font-heading text-2xl text-red-700">${(projections.monthly_expense_avg || 0).toLocaleString()}</p>
            <p className="text-xs text-red-600 mt-1">Average</p>
          </motion.div>
        </div>
      </div>

      {/* Scenario Planning */}
      <div className="bg-white border border-gray-100 p-6">
        <h2 className="font-heading text-xl mb-6">Scenario Planning</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-gray-50 border border-gray-200">
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Conservative</p>
            <p className="font-heading text-3xl text-gray-600">${(scenarios.conservative || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-400 mt-2">20% pipeline conversion</p>
          </div>
          <div className="text-center p-6 bg-[#C6A87C]/10 border border-[#C6A87C]/30">
            <p className="text-xs font-semibold uppercase text-[#C6A87C] mb-2">Base Case</p>
            <p className="font-heading text-3xl text-[#B09265]">${(scenarios.base || 0).toLocaleString()}</p>
            <p className="text-sm text-[#C6A87C] mt-2">30% pipeline conversion</p>
          </div>
          <div className="text-center p-6 bg-green-50 border border-green-200">
            <p className="text-xs font-semibold uppercase text-green-600 mb-2">Aggressive</p>
            <p className="font-heading text-3xl text-green-700">${(scenarios.aggressive || 0).toLocaleString()}</p>
            <p className="text-sm text-green-500 mt-2">50% pipeline conversion</p>
          </div>
        </div>
      </div>

      {/* Goals */}
      <div className="bg-white border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-xl">Business Goals</h2>
        </div>

        {goals.length > 0 ? (
          <div className="space-y-4">
            {goals.map(goal => {
              const categoryConfig = getCategoryConfig(goal.category);
              const CategoryIcon = categoryConfig.icon;
              const progressPercent = Math.min(goal.progress_percentage, 100);
              
              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="border border-gray-100 p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${categoryConfig.color}/10 flex items-center justify-center`}>
                        <CategoryIcon className={`w-5 h-5 ${categoryConfig.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div>
                        <h3 className="font-medium">{goal.title}</h3>
                        {goal.description && (
                          <p className="text-sm text-gray-500">{goal.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold uppercase text-gray-400">{goal.period}</span>
                      <p className="text-sm text-gray-500 mt-1">
                        Due: {new Date(goal.target_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">
                          ${goal.current_value.toLocaleString()} / ${goal.target_value.toLocaleString()}
                        </span>
                        <span className={`font-medium ${progressPercent >= 100 ? 'text-green-600' : progressPercent >= 50 ? 'text-[#C6A87C]' : 'text-gray-600'}`}>
                          {Math.round(progressPercent)}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progressPercent}%` }}
                          transition={{ duration: 0.5 }}
                          className={`h-full ${progressPercent >= 100 ? 'bg-green-500' : progressPercent >= 50 ? 'bg-[#C6A87C]' : 'bg-blue-500'}`}
                        />
                      </div>
                    </div>
                    {progressPercent >= 100 && (
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-4">No goals set yet</p>
            <button
              onClick={() => setShowAddGoal(true)}
              className="text-[#C6A87C] font-semibold hover:underline"
            >
              Create your first goal
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 p-6 hover:border-[#C6A87C] transition-colors cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">View Reports</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 mt-2">Detailed analytics and insights</p>
        </div>

        <div className="bg-white border border-gray-100 p-6 hover:border-[#C6A87C] transition-colors cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">Annual Review</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 mt-2">Year-over-year comparison</p>
        </div>

        <div className="bg-white border border-gray-100 p-6 hover:border-[#C6A87C] transition-colors cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">Growth Forecast</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 mt-2">5-year projection model</p>
        </div>
      </div>

      {/* Add Goal Modal */}
      <AnimatePresence>
        {showAddGoal && (
          <GoalModal
            onClose={() => setShowAddGoal(false)}
            onSave={handleAddGoal}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Goal Modal
const GoalModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_value: '',
    current_value: '0',
    period: 'quarterly',
    category: 'revenue',
    target_date: ''
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl">Add Business Goal</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { 
          e.preventDefault(); 
          onSave({ 
            ...formData, 
            target_value: parseFloat(formData.target_value),
            current_value: parseFloat(formData.current_value) 
          }); 
        }} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Goal Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="Q1 Revenue Target"
              data-testid="goal-title-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
              placeholder="Optional details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                {goalCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Period *</label>
              <select
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                {periods.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Target Value *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={formData.target_value}
                  onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                  required
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Current Value</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={formData.current_value}
                  onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Target Date *</label>
            <input
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]"
              data-testid="save-goal-btn"
            >
              Create Goal
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
